/**
 * Notfall-Schlüssel für Lohndaten (Admin-Rettung, Phase L — Shadow
 * 11.09.2026: „falls alle Chefs die Passwörter vergessen, kann ein Admin
 * retten“). Nur in der Firmenverwaltung des globalen Admins.
 *
 *  - WageEscrowCard: Notfall-Schlüssel einrichten/ersetzen. Der private Teil
 *    wird GENAU EINMAL angezeigt (Server speichert nur den Public Key).
 *  - WageRescueButton: Lohndaten einer Firma mit dem Notfall-Schlüssel für
 *    den aktuellen Chef neu freischalten.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { ApiError } from '../lib/api';
import { companiesApi, type RescueResult } from '../lib/companies';
import { formatDateTime } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';

function Modal({ children, onBackdrop }: { children: ReactNode; onBackdrop?: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onBackdrop?.();
      }}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

export function WageEscrowCard() {
  const token = useAuth((s) => s.token)!;
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['wage-escrow'], queryFn: () => companiesApi.escrowStatus(token) });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);

  async function create() {
    if (busy) return;
    const replace = !!status?.configured;
    if (
      replace &&
      !confirm(
        'Neuen Notfall-Schlüssel erzeugen? Der alte gilt danach nur noch für Werte, die noch nicht neu versiegelt wurden — bewahre ihn trotzdem auf, bis der neue überall angekommen ist.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await companiesApi.createEscrow(token, replace);
      hapticSuccess();
      setNewKey(r.escrowKey);
      await qc.invalidateQueries({ queryKey: ['wage-escrow'] });
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Einrichten fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-1">Notfall-Schlüssel (Lohndaten)</h3>
          {status?.configured ? (
            <p className="text-sm text-slate-300">
              Eingerichtet {status.createdAt ? `am ${formatDateTime(status.createdAt)}` : ''} · Fingerabdruck{' '}
              <code className="font-mono text-xs">{status.fingerprint}</code>
            </p>
          ) : (
            <p className="text-sm text-amber-300">
              Noch keiner eingerichtet — verlieren alle Chefs einer Firma Passwort und Wiederherstellungs-Code, sind
              deren Lohndaten sonst dauerhaft verloren.
            </p>
          )}
          <p className="text-xs text-slate-500 mt-1">
            Der Server speichert nur den öffentlichen Teil. Den geheimen Teil verwahrst du offline (Ausdruck/Tresor) —
            im Alltag sieht auch ein Admin weiterhin nur 0,00 €.
          </p>
        </div>
        <button onClick={() => void create()} disabled={busy || !status} className="btn-ghost text-xs px-3 py-1.5 shrink-0">
          {status?.configured ? 'Ersetzen' : 'Einrichten'}
        </button>
      </div>
      {error && <p className="text-sm text-red-400 mt-2">{error}</p>}
      {newKey && <ShowEscrowKeyModal escrowKey={newKey} onDone={() => setNewKey(null)} />}
    </section>
  );
}

function ShowEscrowKeyModal({ escrowKey, onDone }: { escrowKey: string; onDone: () => void }) {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  function download() {
    const text = [
      'ShiftSync — Notfall-Schlüssel für Lohndaten',
      `Erstellt: ${new Date().toLocaleString('de-DE')}`,
      '',
      escrowKey,
      '',
      'Nur offline aufbewahren (Ausdruck, Tresor, Passwort-Manager). Wer diesen',
      'Schlüssel hat, kann zusammen mit einem Admin-Zugang alle Lohndaten entschlüsseln.',
      '',
    ].join('\r\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shiftsync-notfall-schluessel.txt';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <Modal>
      <h3 className="text-lg font-semibold mb-2">Notfall-Schlüssel</h3>
      <p className="text-sm text-slate-300 mb-3">
        <strong>Wird nur jetzt angezeigt</strong> und nirgends gespeichert. Damit lassen sich die Lohndaten einer Firma
        retten, falls alle Chefs Passwort und Wiederherstellungs-Code verlieren. Gleichzeitig ist er sehr mächtig —
        offline verwahren, nicht per Mail oder Chat verschicken.
      </p>
      <div className="rounded bg-slate-900 border border-amber-500/40 p-3 text-center font-mono text-sm sm:text-base tracking-wider select-all break-all">
        {escrowKey}
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(escrowKey);
              setCopied(true);
            } catch {
              setCopied(false);
            }
          }}
          className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-3 py-1.5"
        >
          {copied ? 'Kopiert ✓' : 'Kopieren'}
        </button>
        <button type="button" onClick={download} className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-3 py-1.5">
          Als Datei speichern
        </button>
      </div>
      <label className="flex items-start gap-2 mt-4 text-sm">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" />
        <span>Ich habe den Notfall-Schlüssel sicher offline abgelegt.</span>
      </label>
      <button type="button" disabled={!confirmed} onClick={onDone} className="btn w-full mt-4 disabled:opacity-50">
        Fertig
      </button>
    </Modal>
  );
}

export function WageRescueButton({ companyId, companyName }: { companyId: string; companyName: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] text-slate-400 hover:text-slate-200 !min-h-0"
        title="Lohndaten mit dem Notfall-Schlüssel neu freischalten"
      >
        Lohndaten retten
      </button>
      {open && <RescueModal companyId={companyId} companyName={companyName} onClose={() => setOpen(false)} />}
    </>
  );
}

function RescueModal({ companyId, companyName, onClose }: { companyId: string; companyName: string; onClose: () => void }) {
  const token = useAuth((s) => s.token)!;
  const [escrowKey, setEscrowKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RescueResult | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await companiesApi.rescueWages(token, companyId, escrowKey);
      hapticSuccess();
      setResult(r);
      setEscrowKey('');
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Rettung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onBackdrop={onClose}>
      <h3 className="text-lg font-semibold mb-2">Lohndaten retten — {companyName}</h3>
      {result ? (
        <div className="space-y-2 text-sm">
          <p className="text-emerald-400">
            Fertig: {result.envelopes} Lohnwerte geprüft, {result.resealed} neu freigeschaltet.
          </p>
          {result.withoutEscrow > 0 && (
            <p className="text-amber-300">
              {result.withoutEscrow} Werte hatten noch kein Notfall-Siegel (älter als der Notfall-Schlüssel und seitdem
              von niemandem geöffnet) — die lassen sich damit nicht retten.
            </p>
          )}
          {result.chefsWithoutKey.length > 0 && (
            <p className="text-amber-300">
              Noch ohne nutzbaren Schlüssel: {result.chefsWithoutKey.join(', ')}. Diese Chefs müssen sich anmelden bzw.
              beim Hinweis „Schlüssel gesperrt“ „Code verloren → Neu beginnen“ wählen — danach die Rettung hier noch
              einmal ausführen.
            </p>
          )}
          <button onClick={onClose} className="btn w-full mt-2">
            Schließen
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <ol className="text-sm text-slate-300 list-decimal pl-5 space-y-1">
            <li>Der Chef setzt sein Passwort über „Passwort vergessen“ zurück und meldet sich an.</li>
            <li>
              Beim Hinweis „Schlüssel gesperrt“ wählt er „Code verloren → Neu beginnen“ (ohne Code-Hinweis ist nichts zu
              tun).
            </li>
            <li>Danach hier den Notfall-Schlüssel eingeben — die Lohndaten werden für ihn neu freigeschaltet.</li>
          </ol>
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Notfall-Schlüssel</span>
            <textarea
              required
              rows={3}
              value={escrowKey}
              onChange={(e) => setEscrowKey(e.target.value)}
              className="input font-mono text-sm"
              placeholder="SSN1-XXXXX-XXXXX-…"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <p className="text-xs text-slate-500">
            Der Schlüssel wird nur für diesen Vorgang verwendet und nicht gespeichert.
          </p>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">
              Abbrechen
            </button>
            <button type="submit" disabled={busy || escrowKey.trim().length < 20} className="btn flex-1 disabled:opacity-50">
              {busy ? '…' : 'Retten'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
